import { z } from 'zod';

import { FIELD_TYPE, POSITION, PRICING_CALCULATE_TYPE, STATUS } from './constants';

export const nameSchema = z.object({
  uz: z.string(),
  ru: z.string(),
  en: z.string(),
  kr: z.string(),
  tr: z.string(),
  default: z.any()
});

export const terraceSchema = z.object({
  id: z.number().optional(),
  area: z.coerce.number().nullable(),
  pricePerArea: z.coerce.number().nullable(),
  pricePerAreaRepaired: z.coerce.number().nullable()
});

export const currencySchema = z.object({
  name: nameSchema,
  ccy: z.string(),
  value: z.number(),
  basic: z.boolean(),
  updateDate: z.any(),
  id: z.number()
});

export const ACTION = z.enum([
  'BLOCK_NEW',
  'BLOCK_UPDATE',
  'BLOCK_DELETE',
  'BLOCK_NUMBERING',
  'BLOCK_INCREMENT',
  'BLOCK_BASEMENT',
  'BLOCK_FLOOR',
  'BLOCK_DUPLICATE',

  'ENTRANCE_NEW',
  'ENTRANCE_UPDATE',
  'ENTRANCE_ADDITION',
  'ENTRANCE_DELETE',
  'ENTRANCE_DUPLICATE',

  'FLAT_UNDO',
  'FLAT_INCREMENT',
  'FLAT_NUMBERING',

  'FIELD_NEW',
  'FIELD_UPDATE'
]);

export const blockSchema = z.object({
  id: z.number(),
  name: z.string().min(1),
  houseId: z.number(),
  houseName: z.string(),
  uuid: z.string(),
  currency: currencySchema,
  floor: z.coerce.number().min(1),
  sale: z.boolean().nullable(),
  startDate: z.string().nullable(),
  finishDate: z.string().nullable(),
  priceCalculateType: z.nativeEnum(PRICING_CALCULATE_TYPE)
});

export const gallerySchema = z.object({
  id: z.number().optional().nullable(),
  position: z.nativeEnum(POSITION).nullable(),
  queue: z.number().default(0).nullable(),
  fileId: z.number().nullable(),
  fileOriginalName: z.string().nullable(),
  fileAbsolutePath: z.string().nullable().optional(),
  fileThumbAbsolutePath: z.string().nullable().optional(),
  fileSmallestAbsolutePath: z.string().nullable().optional(),
  fileSize: z.number().nullable().optional()
});

export const fieldSchema = z.object({
  id: z.number().optional(),
  name: z.string(),
  showPDF: z.boolean().nullable(),
  type: z.nativeEnum(FIELD_TYPE),
  show: z.boolean(),
  options: z.array(z.object({ id: z.number().optional(), name: z.string() })).default([])
});

export const flatFieldSchema = z.object({
  fieldId: z.number(),
  value: z.coerce.number().or(z.coerce.string()).optional(),
  name: z.string(),
  showPDF: z.boolean().nullable()
});

export const optionSchema = z.object({
  id: z.number(),
  nameUZ: z.string(),
  nameRU: z.string(),
  nameEN: z.string(),
  nameUZCR: z.string()
});

export const flatSchema = z.object({
  id: z
    .number()
    .nullish()
    .transform(v => v ?? Math.random()),
  number: z.string().nullable(),
  rooms: z.coerce.number().nullable(),
  area: z.coerce.number().nullable(),
  status: z.nativeEnum(STATUS).nullable(),
  repaired: z.boolean().nullable(),
  apartment: z.boolean().nullable(),
  prepaymentPercent: z.coerce.number().max(100).nullable(),
  prepayment: z.number().nullable(),
  delay: z.coerce.number().nullable(),
  terrace: terraceSchema.nullable().optional().nullable(),
  createdDate: z.string().nullable(),
  deletedDate: z.string().nullable().default('').nullable(),
  price: z.coerce.number().nullable(),
  pricePerArea: z.coerce.number().nullable(),
  pricePerAreaRepaired: z.coerce.number().nullable(),
  priceRepaired: z.coerce.number().nullable(),
  priceNotRepaired: z.coerce.number().nullable(),
  floor: z.number().nullable(),
  col: z.number().nullable(),
  gallery: z.array(gallerySchema).nullable(),
  discountID: z.number().nullable(), // discountId ga ozgartirsh kerak
  fields: z.array(flatFieldSchema).nullable(),
  isNew: z.boolean().optional(),
  category: z.string().nullable().optional(),
  flatCategory: z.string().nullable().optional(),
  livingArea: z.coerce.number().nullable()
});

export const entranceSchema = z.object({
  id: z.number(),
  name: z.string(),
  flatsPerFloor: z.coerce.number(),
  floors: z.coerce.number(),
  flats: z.array(flatSchema),
  orders: z.number().nullable()
});

export type ID = string | number;
export type Entrance = z.infer<typeof entranceSchema>;
export type Block = z.infer<typeof blockSchema>;
export type Flat = z.infer<typeof flatSchema>;
export type Gallery = z.infer<typeof gallerySchema>;
export type Terrace = z.infer<typeof terraceSchema>;
export type Field = z.infer<typeof fieldSchema>;
export type Option = z.infer<typeof optionSchema>;
export type Action = z.infer<typeof ACTION>;
export type FlatField = z.infer<typeof flatFieldSchema>;

export namespace Shaxmatka {
  export type Column = {
    entranceId: number;
    count: number;
    list: Record<number, number[]>;
  };

  export interface Entrance {
    column: Column;
    list: number[];
  }

  export type Row = {
    count: number;
    list: Record<ID, number[]>;
  };

  export interface Main {
    entrance: Record<number, number[]>;
    row: Row;
    column: Record<string, Column>;
    flat: {
      ids: number[];
      list: Record<number, Flat>;
    };
  }
}
